import React from 'react'
import { Formik, FieldArray, FormikProps } from 'formik'
import * as Yup from 'yup'
import get from 'lodash/get'
import { t } from '../../i18n'
import { ServoMoveStep } from './models'

interface ServoMoveStepFormProps {
  servoMoveStep?: ServoMoveStep
  onSubmitForm: (values: any, callback: (success: boolean, errorData: any) => void) => void
  readOnly?: boolean
  formikRef?: React.Ref<FormikProps<ServoMoveStep>>
}

const ServoMoveStepForm: React.FC<ServoMoveStepFormProps> = ({
  servoMoveStep = {
    id: null,
    move_step_type: 'cooking_mode',
    name: '',
    number_of_moves: 0,
    servo_moves: Array(1).fill({
      id: null,
      position: 0,
      velocity: 0,
      acceleration: 0,
      deceleration: 0,
      pause_time: 0,
      step_number: 1,
    }),
  },
  onSubmitForm,
  readOnly = false,
  formikRef,
}) => {

  const getValidationSchema = (context: { move_step_type: string }) =>
    Yup.object().shape({
      name: Yup.string().required(t('servo_move_steps.validations.name')),
      number_of_moves: Yup.number()
        .required(t('servo_move_steps.validations.number_of_moves'))
        .min(1, t('servo_move_steps.validations.min_number_of_moves')),
      move_step_type: Yup.string().oneOf(['cooking_mode', 'unload_mode']), // Define move_step_type explicitly
      servo_moves: Yup.array()
        .of(
          Yup.object().shape({
            position: Yup.number()
              .when('$move_step_type', ([move_step_type]: any, schema) => {
                if (move_step_type === 'cooking_mode') {
                  return schema
                    .required(t('servo_moves.validations.position.required'))
                    .min(0, t('servo_moves.validations.position.min', { min: 0 }))
                    .max(115, t('servo_moves.validations.position.max', { max: 115 }));
                }
                return schema
                  .required(t('servo_moves.validations.position.required'))
                  .min(85, t('servo_moves.validations.position.min', { min: 85 }))
                  .max(115, t('servo_moves.validations.position.max', { max: 115 }));
              }),
            velocity: Yup.number()
              .required(t('servo_moves.validations.velocity.required'))
              .min(0.1, t('servo_moves.validations.velocity.min', { min: 0.1 }))
              .max(5.0, t('servo_moves.validations.velocity.max', { max: 5.0 })),
            acceleration: Yup.number()
              .required(t('servo_moves.validations.acceleration.required'))
              .min(0.1, t('servo_moves.validations.acceleration.min', { min: 0.1 }))
              .max(50.0, t('servo_moves.validations.acceleration.max', { max: 50.0 })),
            deceleration: Yup.number()
              .required(t('servo_moves.validations.deceleration.required'))
              .min(0.1, t('servo_moves.validations.deceleration.min', { min: 0.1 }))
              .max(50.0, t('servo_moves.validations.deceleration.max', { max: 50.0 })),
            pause_time: Yup.number().required(t('servo_moves.validations.pause_time')),
          })
        )
        .max(4, t('servo_moves.validations.max_moves')),
    });

  return (
    <Formik
      innerRef={formikRef}
      initialValues={servoMoveStep}
      validationSchema={getValidationSchema({ move_step_type: servoMoveStep.move_step_type })}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        const payload = { ...values }
        return onSubmitForm(payload, (success, errorData) => {
          let status = {}
          if (!success) {
            const fields = Object.getOwnPropertyNames(errorData.full_errors)
            for (let key in fields) {
              status[`${fields[key]}_error`] =
                errorData.full_errors[fields[key]].join(', ')
            }
          }
          setStatus(status)
          setSubmitting(false)
        })
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setStatus,
      }) => {
        const enhancedHandleChange = (e) => {
          let newStatus = Object.assign({}, status)
          newStatus[`${e.target.name}_error`] = ''
          setStatus(newStatus)
          handleChange(e)
        }

        const renderField = (label, name, type, placeholder, value = null, inverted = true, autoComplete = 'off') => {
          const inputValue = value !== null ? value : values[name]
          const errorsPresent = get(errors, name) && get(touched, name)
          return (
            <div className="form-group">
              <label>{label}</label>
              <input
                id={name}
                name={name}
                type={type}
                onBlur={handleBlur}
                value={inputValue === null ? '' : inputValue}
                placeholder={placeholder}
                autoComplete={autoComplete}
                onChange={enhancedHandleChange}
                className={`form-control ${errorsPresent ? 'is-invalid' : ''} ${inverted ? 'inverted' : ''}`}
                step="any"
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                  {get(errors, name)}
                </div>
              )}
            </div>
          );
        };

        const renderMoveStepTypeRadio = () => {
          return (
            <div style={{
              padding: '10px',
              backgroundColor: 'white',
              borderRadius: '10px',
              marginTop: '10px',
              marginBottom: '20px',
            }}>
              <label style={{
                fontSize: '12px',
                color: '#6F6F6F',
                marginLeft: '5px',
                marginBottom: '10px'
                }}
              >
                {t('servo_move_steps.move_step_type')}
              </label>
              <div>
                {['cooking_mode', 'unload_mode'].map((option, index) => (
                  <div key={index} style={{display: 'block', padding: '7px'}}>
                    <input
                      style={{marginBottom: '2px'}}
                      type="radio"
                      name="move_step_type"
                      id={`move_step_type_${option}`}
                      value={option}
                      checked={values.move_step_type === option}
                      onChange={(e) => {
                        handleChange(e)
                        setStatus({ ...status, move_step_type_error: '' })
                      }}
                    />
                    <label className="form-check-label" htmlFor={`move_step_type_${option}`}>
                      {t(`servo_move_steps.move_step_types.${option}`)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )
        }
        return (
          <form id="servo-move-step-form" onSubmit={handleSubmit} className="form-robis">
            <fieldset disabled={readOnly}>
            {renderMoveStepTypeRadio()}
            {renderField(t('servo_move_steps.name'), 'name', 'text', t('servo_move_steps.name'))}
            {renderField(
              t('servo_move_steps.number_of_moves'),
              'number_of_moves',
              'number',
              t('servo_move_steps.number_of_moves')
            )}

            <FieldArray name="servo_moves">
              {({ remove, push }) => {
                const sortedServoMoves = values.servo_moves
                  .filter((move) => !move._destroy)
                  .sort((a, b) => a.step_number - b.step_number);

                const nextStepNumber =
                  sortedServoMoves.length > 0
                    ? Math.max(...sortedServoMoves.map((move) => move.step_number)) + 1
                    : 1;

                const renumberMoves = (moves) => {
                  const reorderedMoves = moves
                    .filter((move) => !move._destroy)
                    .sort((a, b) => a.step_number - b.step_number)
                    .map((move, index) => ({
                      ...move,
                      step_number: index + 1,
                    }));

                  return reorderedMoves.concat(moves.filter((move) => move._destroy));
                };

                return (
                  <div>
                    <h5 style={{ margin: '40px 0 20px 10px' }}>{t('servo_moves.title')}</h5>
                    {sortedServoMoves.map((_, index) => (
                      <div
                        key={index}
                        className="servo-move-fields"
                        style={{
                          backgroundColor: 'white',
                          padding: '20px',
                          borderRadius: '20px',
                          marginBottom: '20px',
                        }}
                      >
                        <h6 style={{ margin: '10px 0 20px 10px' }}>
                          {t('servo_moves.move')} #{index + 1}
                        </h6>
                        <div className="d-flex flex-wrap align-items-end" style={{ columnGap: '12px' }}>
                          {(['position', 'velocity', 'acceleration', 'deceleration', 'pause_time'] as const).map(
                            (field) => (
                              <div key={field} style={{ flex: '1 1 110px', minWidth: '110px' }}>
                                {renderField(
                                  t(`servo_moves.${field}`),
                                  `servo_moves[${index}].${field}`,
                                  'number',
                                  t(`servo_moves.${field}`),
                                  values.servo_moves[index][field],
                                  false
                                )}
                              </div>
                            )
                          )}
                          <div className="form-group" style={{ flexShrink: 0 }}>
                            <button
                              type="button"
                              style={{ height: '40px' }}
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                const updatedServoMoves = [...values.servo_moves];
                                updatedServoMoves[index]._destroy = true;

                                const renumberedMoves = renumberMoves(updatedServoMoves);

                                setFieldValue('servo_moves', renumberedMoves);
                              }}
                              disabled={sortedServoMoves.length <= 1}
                            >
                              {t('servo_moves.remove')}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {!readOnly && sortedServoMoves.length < 4 && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          const newMove = {
                            position: 0,
                            velocity: 0,
                            acceleration: 0,
                            deceleration: 0,
                            pause_time: 0,
                            step_number: nextStepNumber,
                            _destroy: false,
                          };

                          const updatedServoMoves = [...values.servo_moves, newMove];
                          const renumberedMoves = renumberMoves(updatedServoMoves);

                          setFieldValue('servo_moves', renumberedMoves);
                        }}
                      >
                        <i className="fa fa-plus" style={{ marginRight: '5px' }} /> {t('servo_moves.add')}
                      </button>
                    )}
                  </div>
                );
              }}
            </FieldArray>

            </fieldset>
          </form>
        )
      }}
    </Formik>
  )
}

export default ServoMoveStepForm
